import { RateMasterService } from '../../services';

const initialState = {
  rates: [],
  currentRate: null,
  loading: false,
  error: null,
};

const mutations = {
  SET_LOADING(moduleState, loading) {
    moduleState.loading = loading;
  },
  SET_ERROR(moduleState, error) {
    moduleState.error = error;
  },
  SET_RATES(moduleState, rates) {
    moduleState.rates = rates;
  },
  SET_CURRENT_RATE(moduleState, rate) {
    moduleState.currentRate = rate;
  },
  ADD_RATE(moduleState, rate) {
    moduleState.rates.push(rate);
  },
  UPDATE_RATE(moduleState, updatedRate) {
    const index = moduleState.rates.findIndex((rate) => (
      rate.id === updatedRate.id
      || rate._id === updatedRate._id
      || rate.id === updatedRate._id
      || rate._id === updatedRate.id
    ));
    if (index !== -1) {
      moduleState.rates.splice(index, 1, updatedRate);
    }
  },
  REMOVE_RATE(moduleState, rateId) {
    moduleState.rates = moduleState.rates.filter((rate) => (
      rate.id !== rateId && rate._id !== rateId
    ));
  },
};

const actions = {
  async fetchRates({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const rates = await RateMasterService.getAllRates();
      commit('SET_RATES', rates);
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createRate({ commit }, rateData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const newRate = await RateMasterService.createRate(rateData);
      commit('ADD_RATE', newRate);
      return newRate;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateRate({ commit }, rateData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const recordId = rateData._id || rateData.id;
      const updatedRate = await RateMasterService.updateRate(recordId, rateData);
      commit('UPDATE_RATE', updatedRate);
      return updatedRate;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteRate({ commit }, rate) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const rateId = rate && typeof rate === 'object' ? rate._id || rate.id : rate;
      if (!rateId) {
        throw new Error('Rate ID is missing');
      }

      await RateMasterService.deleteRate(rateId);
      commit('REMOVE_RATE', rateId);
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getRateById({ commit }, rateId) {
    try {
      const rate = await RateMasterService.getRateById(rateId);
      commit('SET_CURRENT_RATE', rate);
      return rate;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
};

const getters = {
  allRates: (moduleState) => moduleState.rates,
  activeRates: (moduleState) => moduleState.rates.filter((rate) => rate.isActive),
  currentRate: (moduleState) => moduleState.currentRate,
  loading: (moduleState) => moduleState.loading,
  error: (moduleState) => moduleState.error,
  getRatesByCategory: (moduleState) => (category) => (
    moduleState.rates.filter((rate) => rate.costCategory === category)
  ),
  getActiveRatesByCategory: (moduleState) => (category) => (
    moduleState.rates.filter((rate) => rate.costCategory === category && rate.isActive)
  ),
  getEffectiveRateForDate: (moduleState) => (category, date) => {
    const checkDate = new Date(date);
    return moduleState.rates.find((rate) => rate.costCategory === category
      && rate.isActive
      && new Date(rate.effectiveFrom) <= checkDate
      && new Date(rate.effectiveTo) >= checkDate);
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
